import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useNavigate } from "@tanstack/react-router"
import { TFunction } from "i18next"
import { useEffect, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { passwordValidationRule, requiredRule } from "@/utilities/form"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"

import styles from "./reset-password.module.css"

export const Route = createFileRoute("/auth/reset-password")({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    email?: string
  } => {
    return {
      email: (search.email as string) || "",
    }
  },
  beforeLoad: ({ search }) => {
    // Redirect to sign-in if email is missing
    if (!search.email) {
      throw redirect({
        to: "/auth/sign-in",
        search,
      })
    }
  },
  component: RouteComponent,
})

interface ResetPasswordFormSchema {
  digit1: string
  digit2: string
  digit3: string
  digit4: string
  digit5: string
  digit6: string
  newPassword: string
  confirmPassword: string
}

// Helper function to log mutation errors to Sentry
function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function useResetPasswordMutation({
  form,
  email,
  qrLookupID,
  redirect,
  t,
}: {
  form: ReturnType<typeof useForm<ResetPasswordFormSchema>>
  email?: string
  qrLookupID?: string
  redirect?: string
  t: TFunction
}) {
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (data: ResetPasswordFormSchema) => {
      if (!email) {
        throw new Error(t("auth.emailRequiredPasswordReset", { ns: "errors" }))
      }

      const code = `${data.digit1}${data.digit2}${data.digit3}${data.digit4}${data.digit5}${data.digit6}`

      await api.auth.resetPassword({
        username: email,
        confirmationCode: code,
        newPassword: data.newPassword,
      })
      return api.auth.signIn({ username: email, password: data.newPassword })
    },
    onSuccess: () => {
      if (redirect) {
        navigate({ to: redirect })
      } else if (qrLookupID) {
        navigate({
          to: "/qr/$qrLookupID/welcome",
          params: { qrLookupID },
        })
      } else {
        navigate({
          to: "/",
        })
      }
    },
    onError: (err: any) => {
      logMutationError(err, {
        flow: "reset-password",
        email,
        qrLookupID,
      })

      if (
        err.message === t("auth.emailRequiredPasswordReset", { ns: "errors" })
      ) {
        form.setError("root", { message: err.message })
      } else {
        handleAuthError(err, form, t)
      }
    },
  })
}

function useResendResetCodeMutation({
  email,
  onSuccess,
  setError,
}: {
  email?: string
  onSuccess?: () => void
  setError: (args: { error: Error; onClose?: () => void }) => void
}) {
  const { t } = useTranslation(["errors", "gated"])

  return useMutation({
    mutationFn: async () => {
      if (!email) {
        throw new Error(t("auth.emailRequiredResend", { ns: "errors" }))
      }

      return api.auth.forgotPassword({
        username: email,
      })
    },
    onSuccess: () => {
      onSuccess?.()
    },
    onError: (err: any) => {
      logMutationError(err, {
        flow: "resend-reset-code",
        email,
      })
      setError({ error: err })
    },
  })
}

function RouteComponent() {
  const { email, qrLookupID, redirect } = Route.useSearch()
  const { t } = useTranslation()

  const form = useForm<ResetPasswordFormSchema>({
    defaultValues: {
      digit1: "",
      digit2: "",
      digit3: "",
      digit4: "",
      digit5: "",
      digit6: "",
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onChange",
  })

  const newPasswordFieldState = form.getFieldState(
    "newPassword",
    form.formState
  )
  const confirmPasswordFieldState = form.getFieldState(
    "confirmPassword",
    form.formState
  )

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const [showCodeSent, setShowCodeSent] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const { setError } = useErrorModal()

  const resetPasswordMutation = useResetPasswordMutation({
    form,
    email,
    qrLookupID,
    redirect,
    t,
  })
  const resendMutation = useResendResetCodeMutation({
    email,
    onSuccess: () => {
      setShowCodeSent(true)
      setResendCountdown(30)
    },
    setError,
  })

  useEffect(() => {
    if (showCodeSent) {
      const timer = setTimeout(() => {
        setShowCodeSent(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [showCodeSent])

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  const onSubmit = (data: ResetPasswordFormSchema) => {
    form.clearErrors("root")
    resetPasswordMutation.mutate(data)
  }

  const handleResendCode = () => {
    if (resendCountdown > 0) return
    form.clearErrors("root")
    resendMutation.mutate()
  }

  const digitFields = [
    "digit1",
    "digit2",
    "digit3",
    "digit4",
    "digit5",
    "digit6",
  ] as const

  const handlePaste = (pastedText: string) => {
    const digits = pastedText.replace(/\D/g, "").slice(0, 6)
    if (digits.length === 0) return
    form.clearErrors("root")

    // Clear all fields first
    digitFields.forEach((fieldName) => {
      form.setValue(fieldName, "", { shouldValidate: false })
    })

    // Fill fields with available digits
    for (let i = 0; i < digits.length && i < digitFields.length; i++) {
      form.setValue(digitFields[i], digits[i], {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

    // Focus the next empty field, or the last field if all are filled
    const focusIndex = Math.min(digits.length, digitFields.length - 1)
    inputRefs.current[focusIndex]?.focus()
  }

  return (
    <>
      <Heading level={1} as={1}>
        {t("auth.resetPassword.heading", { ns: "common" })}
      </Heading>

      <Text level={2} className={styles.descriptionText}>
        {t("auth.resetPassword.description", { ns: "common" })}
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset disabled={resetPasswordMutation.isPending}>
          <Label>
            {t("auth.resetPassword.verificationCode", { ns: "common" })}
          </Label>

          {/* 6-Digit Code Input */}
          <div className={styles.codeInputRow}>
            {digitFields.map((fieldName, index) => (
              <Controller<ResetPasswordFormSchema>
                key={fieldName}
                name={fieldName}
                control={form.control}
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                  pattern: {
                    value: /^[0-9]$/,
                    message: t("common.singleDigit", { ns: "errors" }),
                  },
                }}
                render={({ field: { ref, ...rest } }) => {
                  return (
                    <TextField
                      aria-label={`Verification code digit ${index + 1}`}
                    >
                      <TextField.Input
                        {...rest}
                        innerRef={(el) => {
                          inputRefs.current[index] = el
                          if (typeof ref === "function") ref(el)
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className={styles.codeDigit}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "")

                          // Clear errors when user starts typing
                          if (form.formState.errors.root)
                            form.clearErrors("root")

                          if (val.length > 1) {
                            // Only handle paste if we're not at the last input or if current input is empty
                            if (index < digitFields.length - 1 || !rest.value) {
                              handlePaste(val)
                            } else {
                              // At the last input with existing value - just take the first digit
                              rest.onChange(val.charAt(0))
                            }
                          } else if (val.length === 1) {
                            rest.onChange(val)
                            if (index < digitFields.length - 1) {
                              inputRefs.current[index + 1]?.focus()
                            }
                          } else if (val.length === 0) {
                            rest.onChange("")
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault()
                          const pastedText = e.clipboardData.getData("text")
                          handlePaste(pastedText)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace") {
                            if (rest.value === "") {
                              if (index > 0) {
                                inputRefs.current[index - 1]?.focus()
                              }
                            } else {
                              rest.onChange("")
                            }
                          } else if (e.key === "ArrowLeft" && index > 0) {
                            inputRefs.current[index - 1]?.focus()
                          } else if (
                            e.key === "ArrowRight" &&
                            index < digitFields.length - 1
                          ) {
                            inputRefs.current[index + 1]?.focus()
                          }
                        }}
                      />
                    </TextField>
                  )
                }}
              />
            ))}
          </div>
          <Controller<ResetPasswordFormSchema>
            name="newPassword"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              ...passwordValidationRule(t),
            }}
            render={({
              field: { ref, onChange, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField
                {...rest}
                onChange={(v) => {
                  onChange(v)
                  if (newPasswordFieldState.isTouched) {
                    form.trigger("newPassword")
                  }
                  if (confirmPasswordFieldState.isDirty) {
                    form.trigger("confirmPassword")
                  }
                }}
                isInvalid={invalid}
              >
                <Label>
                  {t("auth.resetPassword.newPassword", { ns: "common" })}
                </Label>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<ResetPasswordFormSchema>
            name="confirmPassword"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              validate: (value) => {
                const newPassword = form.getValues("newPassword")
                return (
                  value === newPassword ||
                  t("common.passwordMismatch", { ns: "errors" })
                )
              },
            }}
            render={({
              field: { ref, onChange, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField
                {...rest}
                onChange={(v) => {
                  onChange(v)
                  if (
                    confirmPasswordFieldState.isTouched ||
                    newPasswordFieldState.isDirty
                  ) {
                    form.trigger("confirmPassword")
                  }
                }}
                isInvalid={invalid}
              >
                <Label>
                  {t("auth.resetPassword.confirmPassword", { ns: "common" })}
                </Label>
                <TextField.Password innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            buttonType="primary"
            type="submit"
            isPending={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending
              ? t("auth.resetPassword.submittingButton", { ns: "common" })
              : t("auth.resetPassword.submitButton", { ns: "common" })}
          </Button>
        </fieldset>
      </form>

      {/* Resend Code Link */}
      <Text level={2} className={styles.resendContainer}>
        {t("auth.resetPassword.didntReceiveCode", { ns: "common" })}{" "}
        <Button
          buttonType="primary"
          presentation="link"
          onPress={handleResendCode}
          isDisabled={
            resendMutation.isPending ||
            resetPasswordMutation.isPending ||
            resendCountdown > 0
          }
          style={{
            textDecorationLine:
              resendMutation.isPending ||
              resetPasswordMutation.isPending ||
              resendCountdown > 0
                ? "none"
                : "underline",
            opacity:
              resendMutation.isPending ||
              resetPasswordMutation.isPending ||
              resendCountdown > 0
                ? 0.5
                : 1,
          }}
        >
          {resendMutation.isPending
            ? t("auth.resetPassword.resendingCode", { ns: "common" })
            : showCodeSent
              ? t("auth.resetPassword.codeSent", { ns: "common" })
              : resendCountdown > 0
                ? `Resend in 00:${resendCountdown.toString().padStart(2, "0")}`
                : t("auth.resetPassword.resendCode", { ns: "common" })}
        </Button>
        .
      </Text>
    </>
  )
}
